import React, { type ReactNode, useState } from 'react';

import { clamp } from 'beeftools';
import { AnimatePresence } from 'framer-motion';

import IconMinus from '@pkg/assets/icons/minus.svg?react';
import IconPlus from '@pkg/assets/icons/plus.svg?react';

import {
    Badge,
    Bubble,
    Content,
    ContentInterior,
    Count,
    Group,
    Header,
    IconWrapper,
    Label,
    LabelWrapper,
    ToggleAction,
} from './FilterGroup.style.ts';

export interface FilterGroupProps {
    label: string;
    children: ReactNode;
    count?: number;
    scrollable?: boolean;
    initialExpanded?: boolean;
}

export function FilterGroup({
    label,
    children,
    count = 0,
    scrollable = false,
    initialExpanded = false,
}: FilterGroupProps) {
    // TODO: We should move `expanded` to a prop so that we are
    // not always rendering the `children` of collapsed groups.
    // Also, we will have more control over "default expanded" groups.
    const [expanded, setExpanded] = useState(initialExpanded);

    function handleToggle() {
        setExpanded((current) => !current);
    }

    const safeCount = clamp(0, count, 999);

    const countMarkup = safeCount ? (
        <Badge>
            <Count>{safeCount}</Count>
        </Badge>
    ) : null;

    const contentMarkup = expanded ? (
        <Content key="FilterGroup-Content" $scrollable={scrollable}>
            <ContentInterior $scrollable={scrollable}>{children}</ContentInterior>
        </Content>
    ) : null;

    return (
        <Group $expanded={expanded}>
            <Header>
                <LabelWrapper>
                    <Label>{label}</Label>
                    {countMarkup}
                </LabelWrapper>

                <ToggleAction pressed={expanded} onAction={handleToggle}>
                    <Bubble>
                        <IconWrapper>{expanded ? <IconMinus /> : <IconPlus />}</IconWrapper>
                    </Bubble>
                </ToggleAction>
            </Header>

            <AnimatePresence initial={false}>{contentMarkup}</AnimatePresence>
        </Group>
    );
}
